import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Check, CheckCircle, Clock, Gift, X, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import type { UpgradeRequestsSearch } from '@/app/routes/upgrade-requests.index';
import { AdminLayout } from '@/app/shell/admin-layout';
import {
  type AdminUpgradeRequest,
  type ListUpgradeRequestsQuery,
  type UpgradeRequestStatus,
  useAdminUpgradeRequests,
  useDeclineUpgradeRequest,
} from '@/shared/api/admin-upgrade-requests';
import { fmtAgo, fmtDateTime } from '@/shared/lib/format';
import {
  Badge,
  Button,
  CopyId,
  ErrorBanner,
  FilterChip,
  PageHeader,
  Pagination,
  TableShell,
  TableStateRow,
  TD,
  TH,
  THEAD,
  TR,
  WIDE,
} from '@/shared/ui/kit';
import { ReasonDialog } from '@/shared/ui/reason-dialog';

import { ApproveDialog } from './approve-dialog';

const PAGE_SIZE = 50;
const TABS: Array<{ key: NonNullable<UpgradeRequestsSearch['status']>; label: string }> = [
  { key: 'pending', label: 'Pending' },
  { key: 'granted', label: 'Approved' },
  { key: 'declined', label: 'Declined' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'all', label: 'All' },
];

/**
 * Upgrade-request inbox.  Online payment is off, so a paid plan starts with an
 * in-app "Request upgrade"; the operator approves (grants a plan for a period)
 * or declines with a note.  Every decision is audit-logged.
 */
export function UpgradeRequestsListPage() {
  const search = useSearch({ from: '/upgrade-requests/' });
  const navigate = useNavigate({ from: '/upgrade-requests/' });
  const status = search.status ?? 'pending';
  const offset = search.offset ?? 0;

  const query: ListUpgradeRequestsQuery = { limit: PAGE_SIZE, offset };
  if (status !== 'all') query.status = status as UpgradeRequestStatus;
  const { data, isLoading, isFetching, error, refetch } = useAdminUpgradeRequests(query);

  const [approve, setApprove] = useState<AdminUpgradeRequest | null>(null);
  const [decline, setDecline] = useState<AdminUpgradeRequest | null>(null);
  const declineMut = useDeclineUpgradeRequest();

  return (
    <AdminLayout>
      <PageHeader
        title="Upgrade requests"
        description="Customers ask for a paid plan in the app (online payment is off). Approve to grant the plan for a period, or decline with a note. The customer is not emailed automatically — reply to them yourself."
      />

      <div className="mb-3 flex flex-wrap items-center gap-2" role="tablist" aria-label="Status">
        {TABS.map((t) => (
          <FilterChip
            key={t.key}
            label={t.label}
            active={status === t.key}
            onClick={() =>
              void navigate({ search: { status: t.key === 'pending' ? undefined : t.key }, replace: true })
            }
          />
        ))}
        <div className="ml-auto text-xs text-neutral-500" aria-live="polite">
          {isFetching ? 'Loading…' : data ? `${data.total} ${status === 'all' ? 'total' : status}` : null}
        </div>
      </div>

      {error && data ? <ErrorBanner error={error} onRetry={() => void refetch()} /> : null}

      <TableShell>
        <thead className={THEAD}>
          <tr>
            <th className={TH}>Customer</th>
            <th className={TH}>Plan</th>
            <th className={`${TH} ${WIDE}`}>Requested by</th>
            <th className={`${TH} ${WIDE}`}>Note</th>
            <th className={TH}>When</th>
            <th className={`${TH} text-right`}>{status === 'pending' ? 'Decide' : 'Outcome'}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-800">
          <TableStateRow
            colSpan={6}
            loading={isLoading && !data}
            error={!data ? error : undefined}
            empty={Boolean(data && data.items.length === 0)}
            emptyText={status === 'pending' ? 'Inbox zero — no pending requests.' : 'No requests with this status.'}
          />
          {data?.items.map((r) => (
            <RequestRow key={r.id} r={r} onApprove={() => setApprove(r)} onDecline={() => setDecline(r)} />
          ))}
        </tbody>
      </TableShell>

      {data ? (
        <Pagination
          total={data.total}
          offset={offset}
          pageSize={PAGE_SIZE}
          onChange={(o) => void navigate({ search: (p) => ({ ...p, offset: o || undefined }) })}
        />
      ) : null}

      {approve ? <ApproveDialog request={approve} label={labelOf(approve)} onClose={() => setApprove(null)} /> : null}

      {decline ? (
        <ReasonDialog
          open
          onOpenChange={(o) => !o && setDecline(null)}
          title="Decline upgrade request?"
          description={`Rejects the ${decline.requestedPlanCode} request from ${labelOf(decline)}. They can send a new request afterwards.`}
          confirmLabel="Decline"
          tone="danger"
          reasonLabel="Note"
          placeholder="Why — the customer’s billing API can read this note"
          pending={declineMut.isPending}
          onConfirm={async (reason) => {
            await declineMut.mutateAsync({ id: decline.id, reason });
            toast.success('Request declined');
            setDecline(null);
          }}
        />
      ) : null}
    </AdminLayout>
  );
}

function labelOf(r: AdminUpgradeRequest): string {
  return r.customerLabel ?? `${r.customerType} ${r.customerId.slice(0, 8)}…`;
}

function RequestRow(props: { r: AdminUpgradeRequest; onApprove: () => void; onDecline: () => void }) {
  const r = props.r;
  return (
    <tr className={TR}>
      <td className={TD}>
        <div className="text-[10px] uppercase text-neutral-500">{r.customerType === 'team' ? 'team' : 'personal'}</div>
        {r.customerType === 'team' ? (
          <Link to="/teams/$id" params={{ id: r.customerId }} className="text-neutral-100 hover:text-orange-300 hover:underline">
            {labelOf(r)}
          </Link>
        ) : (
          <Link to="/users/$id" params={{ id: r.customerId }} className="text-neutral-100 hover:text-orange-300 hover:underline">
            {labelOf(r)}
          </Link>
        )}
        {/* On phones the requester column is hidden — keep the contact visible here. */}
        <div className="break-all text-xs text-neutral-500 sm:hidden">{r.requestedByEmail}</div>
      </td>
      <td className={`${TD} whitespace-nowrap text-neutral-100`}>{r.requestedPlanCode}</td>
      <td className={`${TD} ${WIDE}`}>
        <Link to="/users/$id" params={{ id: r.requestedBy }} className="text-neutral-300 hover:text-orange-300">
          {r.requestedByName ?? '—'}
        </Link>
        {r.requestedByEmail ? (
          <div>
            <CopyId value={r.requestedByEmail} label="email" className="px-0" />
          </div>
        ) : null}
      </td>
      <td className={`${TD} ${WIDE} max-w-xs text-neutral-400`}>
        {r.note ? <span className="line-clamp-3 italic" title={r.note}>{r.note}</span> : <span className="text-neutral-600">—</span>}
      </td>
      <td className={`${TD} whitespace-nowrap text-neutral-400`} title={fmtDateTime(r.createdAt)}>
        {fmtAgo(r.createdAt)}
      </td>
      <td className={`${TD} text-right`}>
        {r.status === 'pending' ? (
          <div className="flex flex-wrap justify-end gap-1.5">
            <Button tone="ok" icon={<Check size={13} />} onClick={props.onApprove} className="px-2 py-1 text-xs">
              Approve
            </Button>
            <Button icon={<X size={13} />} onClick={props.onDecline} className="px-2 py-1 text-xs">
              Decline
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-end gap-0.5">
            <StatusBadge status={r.status} />
            {r.decidedAt ? <span className="text-[11px] text-neutral-500">{fmtAgo(r.decidedAt)}</span> : null}
            {r.decisionNote ? (
              <span className="max-w-[14rem] truncate text-[11px] italic text-neutral-500" title={r.decisionNote}>
                {r.decisionNote}
              </span>
            ) : null}
          </div>
        )}
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: UpgradeRequestStatus }) {
  const map = {
    pending: { tone: 'warn', icon: <Clock size={10} />, label: 'pending' },
    granted: { tone: 'ok', icon: <CheckCircle size={10} />, label: 'approved' },
    declined: { tone: 'danger', icon: <XCircle size={10} />, label: 'declined' },
    cancelled: { tone: 'neutral', icon: <Gift size={10} />, label: 'cancelled' },
  } as const;
  const m = map[status];
  return (
    <Badge tone={m.tone}>
      {m.icon} {m.label}
    </Badge>
  );
}
